/*
 * ARANOX — Plataforma Digital Corporativa
 * ARX_BS_018.06.03.02 — CORPORATE PERMISSION CLIENT
 * Camada de comunicação frontend → Permission Backend.
 */

import type { ConfigApi, DiscoveryApi, IdentityApi } from '@backstage/core-plugin-api';
import {
  AuthorizeResult,
  PermissionClient,
  createPermission,
  type ResourcePermission,
} from '@backstage/plugin-permission-common';

export const corporateOrganizationIds = [
  'aranox',
  'tes',
  'agrion',
  'sentrix',
  'vitalis',
  'cyrion',
  'cyrius',
] as const;

export type CorporateOrganizationId = (typeof corporateOrganizationIds)[number];

export const corporateModuleIds = [
  'governance',
  'iam',
  'catalog',
  'projects',
  'operations',
  'monitoring',
  'analytics',
  'automation',
  'documentation',
  'administration',
] as const;

export type CorporateModuleId = (typeof corporateModuleIds)[number];
export type CorporateAction = 'view' | 'create' | 'edit' | 'delete' | 'execute' | 'administer';
export const corporatePermissionResourceType = 'aranox-corporate-organization' as const;

const permissionCache = new Map<string, ResourcePermission<typeof corporatePermissionResourceType>>();

type BackstagePermissionAction = 'create' | 'read' | 'update' | 'delete';

function permissionAttributes(
  action: CorporateAction,
): { action?: BackstagePermissionAction } {
  switch (action) {
    case 'view':
      return { action: 'read' };
    case 'create':
      return { action: 'create' };
    case 'edit':
      return { action: 'update' };
    case 'delete':
      return { action: 'delete' };
    case 'execute':
    case 'administer':
      return {};
  }
}

export function createCorporatePermission(
  module: CorporateModuleId,
  action: CorporateAction,
): ResourcePermission<typeof corporatePermissionResourceType> {
  const key = `${module}.${action}`;
  const cached = permissionCache.get(key);
  if (cached) return cached;

  const permission = createPermission({
    name: key,
    attributes: permissionAttributes(action),
    resourceType: corporatePermissionResourceType,
  });

  permissionCache.set(key, permission);
  return permission;
}

export function corporateOrganizationResourceRef(
  organization: CorporateOrganizationId,
): string {
  return `organization:${organization}`;
}

export interface CorporatePermissionCheck {
  organization: CorporateOrganizationId;
  module: CorporateModuleId;
  action?: CorporateAction;
}

export interface CorporatePermissionCheckResult extends CorporatePermissionCheck {
  allowed: boolean;
}

export interface CorporatePermissionClientOptions {
  configApi: ConfigApi;
  discoveryApi: DiscoveryApi;
  identityApi: IdentityApi;
}

export class CorporatePermissionClient {
  private readonly permissionClient: PermissionClient;
  private readonly identityApi: IdentityApi;

  constructor(options: CorporatePermissionClientOptions) {
    this.permissionClient = new PermissionClient({
      config: options.configApi,
      discovery: options.discoveryApi,
    });
    this.identityApi = options.identityApi;
  }

  async authorize(check: CorporatePermissionCheck): Promise<CorporatePermissionCheckResult> {
    const action = check.action ?? 'view';
    const credentials = await this.identityApi.getCredentials();
    const [response] = await this.permissionClient.authorize(
      [{
        permission: createCorporatePermission(check.module, action),
        resourceRef: corporateOrganizationResourceRef(check.organization),
      }],
      { token: credentials.token },
    );

    return {
      ...check,
      action,
      allowed: response?.result === AuthorizeResult.ALLOW,
    };
  }

  async authorizeMany(
    checks: readonly CorporatePermissionCheck[],
  ): Promise<CorporatePermissionCheckResult[]> {
    if (checks.length === 0) return [];

    const credentials = await this.identityApi.getCredentials();
    const responses = await this.permissionClient.authorize(
      checks.map(check => ({
        permission: createCorporatePermission(check.module, check.action ?? 'view'),
        resourceRef: corporateOrganizationResourceRef(check.organization),
      })),
      { token: credentials.token },
    );

    return checks.map((check, index) => ({
      ...check,
      action: check.action ?? 'view',
      allowed: responses[index]?.result === AuthorizeResult.ALLOW,
    }));
  }

  async canAccessOrganization(organization: CorporateOrganizationId): Promise<boolean> {
    const result = await this.authorize({ organization, module: 'catalog', action: 'view' });
    return result.allowed;
  }

  async canAccessAnyOrganization(
    module: CorporateModuleId,
    action: CorporateAction = 'view',
  ): Promise<boolean> {
    const results = await this.authorizeMany(
      corporateOrganizationIds.map(organization => ({ organization, module, action })),
    );
    return results.some(result => result.allowed);
  }
}
